import { useEffect, useRef, useState } from 'react';
import {
  useGetSectionByIdQuery,
  usePutSectionMutation,
} from '@/services/plaintiffSheets/plaintiffSheetsEndpoint';
import { useNavigate } from 'react-router-dom';
import Certifications, { CertificationsHandle } from '../certifications/certifications';
import Button from '@/components/Button/Button';
import CustomTextInput from '@/pages/plaintiffSheets/components/InputFields/CustomTextInput';

import CustomConditionInput from '@/pages/plaintiffSheets/components/InputFields/CustomConditionInput';
import { getAuthData } from '@/utils/auth';
import { useGetSectionsDataQuery } from '@/services/userService/userServiceEndpoints';
import CustomInformationField from '@/pages/plaintiffSheets/components/InputFields/CustomInformationInput';
import CustomDropdownInput from '@/pages/plaintiffSheets/components/InputFields/CustomDropdownInput';
import CustomDateInput from '@/pages/plaintiffSheets/components/InputFields/CustomDateInput';
import { useToast } from '@/components/Toast/ToastContext';
import Loader from '@/components/Loader/Loader';
import CustomDataTable from '@/pages/plaintiffSheets/components/InputFields/CustomDataTable';
import { useDispatch, useSelector } from 'react-redux';
import { setSectionId } from '@/reducers/sectionIdSlice';
import CustomTabsWithTable from '@/pages/plaintiffSheets/components/InputFields/CustomTabsWithTable';
import CollaborationPopup from '../popup/CollaborationPopup';
import SectionLock from '../collaborationSectionLock/SectionLock';
import CustomLabel from '@/pages/plaintiffSheets/components/InputFields/CustomLabel';
import UnSavedProgressPopupNext from '../popup/UnSavedProgressPopupNext';
import UnSavedProgressPopupPrevious from '../popup/UnSavedProgressPopupPrevious';
import { setHasUnsavedChanges } from '@/reducers/formSlice';
import ErrorPage from '../errorPage/ErrorPage';
import { RootState } from '@/store';
import CustomMultipleField from '@/pages/plaintiffSheets/components/InputFields/CustomMultipleField';
import { setDataTableUpdated } from '@/reducers/dataTableUpdatedSlice';
import { trackEvent } from '@/utils/track-event';
import { PostHogEvents } from '@/utils/PostHogEvents';
import { PostHogMessages } from '@/utils/PostHogMessages';
import CustomReadTextArea from '@/pages/plaintiffSheets/components/InputFields/CustomReadTextArea';

import { SourceData, TransformedData } from './UserDynamicFormTypes';
import { getSectionSlug } from '@/utils/slug';
import UserCustomerTextArea from '@/pages/plaintiffSheets/components/InputFields/UserCustomerTextArea';
import CustomFileUpload from '@/pages/plaintiffSheets/components/InputFields/CustomeFileUpload';

const UserDynamicForm = () => {
  const { districtId, email } = getAuthData();
  const sectionId = useSelector((state: any) => state.sectionId.sectionId);
  const certificationsRef = useRef<CertificationsHandle>(null);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const isReviewForm = useSelector((state: RootState) => state.reviewForm.isReviewForm);
  const dataTableUpdated = useSelector(
    (state: RootState) => state.dataTableUpdated.isDataTableUpdated
  );
  const [isUnsavedProgressPopupNextVisible, setUnsavedProgressPopupNextVisible] =
    useState<boolean>(false);
  const [isUnsavedProgressPopupPreviousVisible, setUnsavedProgressPopupPreviousVisible] =
    useState<boolean>(false);
  const certificationDisabled = useSelector(
    (state: RootState) => state?.progress.progressCheck || false
  );

  const hasUnsavedChanges = useSelector(
    (state: RootState) => state?.form?.hasUnsavedChanges || false
  );

  const editContainerRef = useRef<HTMLDivElement>(null);

  // Add this useEffect hook to handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editContainerRef.current && !editContainerRef.current.contains(event.target as Node)) {
        setIsEditingSection(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Collaboration Section Lock Hook
  const {
    handleEdit,
    lockedOwner,
    showCollaborationPopup,
    action,
    setShowCollaborationPopup,
    isLockedByMe,
    setIsEditingSection,
    handleGlobalClick,
    sectionUpdates,
    isConnected,
    checkLockStatus,
  } = SectionLock();

  // Get Section By Id Query
  const {
    data: fieldData,
    isFetching: isApiFetching,
    isLoading: isFieldDataLoading,
    isError,
  } = useGetSectionByIdQuery(
    {
      districtId: districtId,
      sectionId: sectionId ? sectionId : '',
    },
    {
      skip: !districtId || !sectionId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Tab Title
  const title = fieldData?.data?.name;
  const formId = fieldData?.data?.sectionId;
  useEffect(() => {
    if (title) {
      document.title = `${title} | Plaintiff Fact Sheet`;
    }
    // Track PostHog event
    trackEvent(PostHogEvents.FORM_SECTION_VIEWED, {
      section_name: title,
      form_id: formId,
      timestamp: new Date().toISOString(),
      message: PostHogMessages[PostHogEvents.FORM_SECTION_VIEWED],
    });
  }, [title]);

  const handleClick = () => {
    certificationsRef.current?.triggerSubmit();
  };
  const { data: sectionsData } = useGetSectionsDataQuery(
    {
      districtId: districtId,
    },
    {
      skip: !districtId,
      refetchOnMountOrArgChange: true,
    }
  );

  const navigate = useNavigate();
  const { addToast } = useToast();
  const dispatch = useDispatch();
  const factSheetStatus = useSelector((state: RootState) => state.factSheetStatus.status);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const { districtName } = getAuthData();
  const formPages: { section_id: string; section_name: string }[] =
    sectionsData?.data?.section_responses || [];
  const [updateSection, { isLoading: isUpdateSectionLoading }] = usePutSectionMutation();

  // Data Type
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [conditionalValues, setConditionalValues] = useState<Record<string, any>>({});
  const [tablesData, setTablesData] = useState<Record<string, any>>({});
  const [tabsData, setTabsData] = useState<Record<string, any>>({});
  const [fieldValue, setFieldValue] = useState({});

  // Get all fields and tables, then sort them by order
  const allFields = fieldData?.data?.fields || [];
  const dataTables =
    sectionUpdates?.sectionId == sectionId
      ? sectionUpdates?.data_tables
      : fieldData?.data?.data_tables || [];
  const tabsTables =
    sectionUpdates?.sectionId == sectionId ? sectionUpdates?.tabs : fieldData?.data?.tabs || [];

  // Filter main fields (non-conditional)
  const mainFields = allFields.filter((field: any) => field.fieldScope !== 'condition');

  // Combine fields, data tables, and tabs tables into a single array for ordered rendering
  const allComponents = [...mainFields, ...dataTables, ...tabsTables].sort(
    (a, b) => a.order - b.order
  );

  // Reset form values whenever fieldData changes
  useEffect(() => {
    if (!allFields.length) return;

    const initialConditionalValues: Record<string, any> = {};

    allFields.forEach((field: any) => {
      // Handle conditional fields and their options
      if (field.fieldType === 'condition') {
        // Find the selected option for this condition field
        const selectedOption = field.value;

        if (selectedOption) {
          // Find the option object with this value
          const optionObj = field.options?.find((opt: any) => opt.value === selectedOption);

          if (optionObj && optionObj.field_id) {
            // Find the nested field value if it exists
            const nestedField = allFields.find((f: any) => f.id === optionObj.field_id);

            if (nestedField) {
              // Store with nested field structure
              initialConditionalValues[field.id] = {
                value: selectedOption,
                nested_field: {
                  field_id: nestedField.id,
                  value: nestedField.value || '',
                },
              };
            } else {
              // Store just the main value
              initialConditionalValues[field.id] = {
                value: selectedOption,
              };
            }
          }
        }
      }
    });

    setConditionalValues(initialConditionalValues);
  }, [fieldData, sectionUpdates, isApiFetching]);

  // For fields that are not conditional, set their initial values
  useEffect(() => {
    if (!allFields.length) return;

    allFields.forEach((field: any) => {
      formValues[field.field_id] = field.value || '';
    });
  }, [fieldData, isApiFetching]);

  function transformDataFormat(sourceDataArray: SourceData[]): TransformedData {
    // Map each source data item to its transformed format
    const transformedTabFields = sourceDataArray.map((sourceData) => {
      // Transform each tab response
      const tabs = sourceData.tab_responses.map((tabResponse) => {
        // Process fields
        const fields = tabResponse.field_responses.map((field) => {
          return {
            field_id: field.field_id,
            value: field.value || '',
            values: field.values || [],
          };
        });

        // Process tables
        const tables = tabResponse.table_responses.map((table) => {
          // Transform the values to the required format
          const transformedValues = table.values.map((value) => {
            return {
              row_id: '', // Generate a unique row_id
              row_order: value.row_order,
              column_id: value.column_id,
              column_order: value.column_order,
              value: value.value || '',
            };
          });

          return {
            table_id: table.table_id,
            values: transformedValues,
          };
        });

        return {
          tab_id: tabResponse.tab_id,
          fields: fields,
          tables: tables,
        };
      });

      return {
        tab_field_id: sourceData.tab_field_id,
        tabs: tabs,
      };
    });

    // Construct the final result
    return {
      tabFields: transformedTabFields,
    };
  }

  // ----------------------------Handle change events for the data types ----------------------------- //
  const handleChange = (fieldId: string) => (e: React.ChangeEvent<any>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormValues((prev) => {
      const updated = { ...prev, [fieldId]: value };
      return updated;
    });
  };

  const handletextAreaChange = (fieldId: string) => (e: React.ChangeEvent<any>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    dispatch(setHasUnsavedChanges(true));
    dispatch(setDataTableUpdated(false));
    setFormValues((prev) => {
      const updated = { ...prev, [fieldId]: value };
      return updated;
    });
  };

  const handleDateOrDropdownChange =
    (fieldId: string) => (e: { target: { value: string; name: string; type: string } }) => {
      const value = e.target.type === 'checkbox' ? e.target.value == 'true' : e.target.value;
      dispatch(setHasUnsavedChanges(true));
      dispatch(setDataTableUpdated(false));
      setFormValues((prev) => {
        const updated = { ...prev, [fieldId]: value };
        return updated;
      });
    };

  // Function to handle table data changes
  const handleTableDataChange = (tableData: any) => {
    if (!tableData.isInitial) {
      dispatch(setHasUnsavedChanges(true));
    }

    setTablesData((prev) => ({
      ...prev,
      [tableData.table_id]: tableData.values,
    }));
    setFieldValue((prev) => ({
      ...prev,
      ...tableData.nested_fields,
    }));
  };

  const handleTabsDataChange = (tableData: any) => {
    setTabsData((prev) => ({
      ...prev,
      [tableData.tab_field_id]: tableData.tab_responses,
    }));
  };

  // Updated to store conditional values separately
  const handleConditionChange = (fieldId: string, value: any) => {
    dispatch(setHasUnsavedChanges(true));
    dispatch(setDataTableUpdated(false));

    setConditionalValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleConditionWithMultipleFields = (fieldId: string, value: any) => {
    setConditionalValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const shouldHighlightTab = (tab: any) => {
    const statusRequiresHighlight =
      factSheetStatus &&
      ['user_review_requested', 'review_and_edits_requested', 'user_edits_requested'].includes(
        factSheetStatus
      );

    if (!statusRequiresHighlight) return false;

    // Check if tab itself is marked for review
    if (tab.is_admin_review === true) return true;

    // Check field_responses within the tab
    if (tab.field_responses && Array.isArray(tab.field_responses)) {
      const hasHighlightedField = tab.field_responses.some(
        (field: any) => field.is_admin_review === true
      );
      if (hasHighlightedField) return true;
    }

    // Check table_responses within the tab
    if (tab.table_responses && Array.isArray(tab.table_responses)) {
      const hasHighlightedTable = tab.table_responses.some(
        (table: any) => table.is_admin_review === true
      );
      if (hasHighlightedTable) return true;
    }

    return false;
  };

  // Function to check if a table should be highlighted
  const shouldHighlightTable = (table: any) => {
    const statusRequiresHighlight =
      factSheetStatus &&
      ['user_review_requested', 'review_and_edits_requested', 'user_edits_requested'].includes(
        factSheetStatus
      );

    if (!statusRequiresHighlight) return false;

    return table.is_admin_review === true;
  };

  // Function to check if tab field should be highlighted (for the entire tab field component)
  const shouldHighlightTabField = (tabField: any) => {
    const statusRequiresHighlight =
      factSheetStatus &&
      ['user_review_requested', 'review_and_edits_requested', 'user_edits_requested'].includes(
        factSheetStatus
      );

    if (!statusRequiresHighlight) return false;

    // Check if the tab field itself is marked for review
    if (tabField.is_admin_review === true) return true;

    // Check if any of the tab_responses should be highlighted
    if (tabField.tab_responses && Array.isArray(tabField.tab_responses)) {
      return tabField.tab_responses.some((tab: any) => shouldHighlightTab(tab));
    }

    return false;
  };

  const handleUpload = (fieldId: string, value: any) => {
    dispatch(setHasUnsavedChanges(true));

    setConditionalValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };
  // ----------------------------Handle change events for the data types ----------------------------- //

  // Function to reset all form data when navigating to a new section
  const resetFormData = () => {
    // setFormValues({});
    setTablesData({});
    setTabsData({});
    setConditionalValues({});
  };

  // ---------------------------- Rendering the fields based on their type ------------------------------- //

  const renderField = (field: any, highlight = false) => {
    const highlightClass = highlight ? 'ring-2 ring-[#EF8B21] bg-[#FCF2E6]' : '';

    const handleFocus = () => {
      trackEvent(PostHogEvents.FORM_FIELD_FOCUSED, {
        field_name: field.label,
        form_id: field.field_id, // Make sure formId is in scope
        message: PostHogMessages[PostHogEvents.FORM_FIELD_FOCUSED],
      });
    };

    switch (field.field_type) {
      case 'text_input':
        return (
          <CustomTextInput
            label={field.label}
            name={field.field_id}
            value={
              field?.label?.includes('1. Plaintiff')
                ? districtName
                : formValues[field.field_id]
                  ? formValues[field.field_id]
                  : ''
            }
            onChange={handleChange(field.field_id)}
            placeholder={field.additional_config?.placeholder || ''}
            required={field.is_required}
            description={field.additional_config?.helpText}
            width="w-[350px]"
            isEditable={field.additional_config?.editable}
            defaultValue={field?.additional_config?.defaultValue}
            className={`p-1 ${highlightClass}`}
            isLockedByMe={isLockedByMe}
            lockedOwner={lockedOwner}
            isReviewForm={isReviewForm}
            onFocus={handleFocus}
          />
        );

      case 'text_area':
        return (
          <>
            <UserCustomerTextArea
              label={field.label}
              name={field.field_id}
              value={formValues[field.field_id] ?? ''}
              onChange={handletextAreaChange(field.field_id)}
              placeholder={field.additional_config?.placeholder || ''}
              required={field.is_required}
              description={field.additional_config?.helpText}
              rows={8}
              isCondition={false}
              isLockedByMe={isLockedByMe}
              lockedOwner={lockedOwner}
              isReviewForm={isReviewForm}
              onFocus={handleFocus}
              className={highlightClass}
            />
          </>
        );
      case 'dropdown':
        return (
          <CustomDropdownInput
            label={field.label}
            name={field.field_id}
            value={formValues[field.field_id] ?? ''}
            options={field.options || []}
            onChange={handleDateOrDropdownChange(field.field_id)}
            placeholder={field.additional_config?.placeholder || 'Select an option'}
            required={field.is_required}
            description={field.additional_config?.helpText}
            width="w-[400px]"
            isEditable={field.additional_config?.editable !== false}
            className={`p-1 ${highlightClass}`}
            onFocus={handleFocus}
          />
        );
      case 'date':
        return (
          <CustomDateInput
            label={field.label}
            name={field.field_id}
            value={formValues[field.field_id] ?? ''}
            onChange={handleDateOrDropdownChange(field.field_id)}
            placeholder={field.additional_config?.placeholder || 'Select Date'}
            required={field.is_required}
            description={field.additional_config?.helpText}
            width="w-[400px]"
            isEditable={field.additional_config?.editable !== false}
            className={`p-1 ${highlightClass}`}
            dateFormat={field.additional_config?.dateFormat || 'dd-mm-yyyy'}
            onFocus={handleFocus}
          />
        );

      case 'information':
        return (
          <CustomInformationField
            label={field.label}
            sectionName={fieldData?.data?.name}
            description={field.additional_config?.helpText}
            beforeLinkDescription={field.additional_config?.beforeLinkDescription}
            inlineTriggerText={field.additional_config?.inlineTriggerText}
            modalContent={field.additional_config?.modalContent}
            table_response={field.table_responses}
            onFocus={handleFocus}
            className={`${highlightClass}`}
          />
        );
      case 'condition':
      case 'condition_with_table':
        return (
          <div className="space-y-2">
            <CustomConditionInput
              sectionData={fieldData?.data}
              label={field.label}
              options={field.options}
              onChange={handleConditionChange}
              required={field.is_required}
              description={field.additional_config?.helpText}
              isLockedByMe={isLockedByMe}
              lockedOwner={lockedOwner}
              isReviewForm={isReviewForm}
              defaultValue={field?.additional_config?.defaultValue}
              value={field.value}
              onFocus={handleFocus}
              field={field}
              // Fix: Pass the function references, not wrapped functions
              shouldHighlightOption={shouldHighlightOption}
              shouldHighlightNestedField={shouldHighlightNestedField}
              shouldHighlightFieldInOption={shouldHighlightFieldInOption}
              className={`p-1 ${highlight ? 'ring-2 ring-[#EF8B21] bg-[#FCF2E6]' : ''}`}
            />
          </div>
        );
      case 'file':
        return (
          <CustomFileUpload
            label={field.label}
            value={fieldData}
            name={field.label}
            helpText={field.additional_config?.helpText}
            onChange={(fieldId, fileData) => handleUpload(fieldId, fileData)}
            isLockedByMe={isLockedByMe}
            lockedOwner={lockedOwner}
            isReviewForm={isReviewForm}
            values={field.files}
            onFocus={handleFocus}
            fieldId={field.field_id}
            className={`${highlightClass}`}
            shouldHighlightNestedField={shouldHighlightNestedField}
          />
        );

      case 'condition_with_multiple_fields':
        return (
          <div className="space-y-2">
            <CustomMultipleField
              fieldId={field?.field_id}
              value={field.value}
              name={field.label}
              label={field?.label}
              options={field?.options}
              isReviewForm={isReviewForm}
              sectionData={fieldData}
              onChange={handleConditionWithMultipleFields}
              defaultValue={field?.additional_config?.defaultValue}
              helpText={field?.additional_config?.helpText}
              onFocus={handleFocus}
              isPdf={false}
              field={field}
              shouldHighlightOption={shouldHighlightOption}
              shouldHighlightNestedField={shouldHighlightNestedField}
              shouldHighlightFieldInOption={shouldHighlightFieldInOption}
              className={`p-1 ${highlight ? 'ring-2 ring-[#EF8B21] bg-[#FCF2E6]' : ''}`}
            />
          </div>
        );

      case 'readonly_text_area':
        return (
          <CustomReadTextArea label={field.label} data={field} className={`${highlightClass}`} />
        );

      case 'label':
        return <CustomLabel name={field.label} className={`${highlightClass}`} />;

      default:
        return (
          <p className={`text-red-500 ${highlight}`}>Unsupported field type: {field.fieldType}</p>
        );
    }
  };

  // ---------------------------- Rendering the fields based on their type ------------------------------- //

  // Add this useEffect to sync currentStep with the URL parameter
  useEffect(() => {
    if (sectionId && formPages.length > 0) {
      const currentIndex = formPages.findIndex((page) => page.section_id === sectionId);
      if (currentIndex !== -1) {
        setCurrentStep(currentIndex);
        resetFormData();
        setUnsavedProgressPopupNextVisible(false);
        setUnsavedProgressPopupPreviousVisible(false);
      }
    }
  }, [sectionId, formPages]);

  // Check lock status when sectionId changes
  useEffect(() => {
    if (sectionId && isConnected) {
      checkLockStatus(sectionId);
      handleGlobalClick();
    }
  }, [sectionId, isConnected]);

  const handleNext = () => {
    if (currentStep < formPages.length - 1) {
      resetFormData();
      setIsEditingSection(false);
      dispatch(setHasUnsavedChanges(false));
      handleGlobalClick();
      dispatch(setSectionId(formPages[currentStep + 1].section_id));
      const nextStep = currentStep + 1;
      // Use section name slug in the URL
      const nextSectionSlug = getSectionSlug(formPages[nextStep].section_name);
      navigate(`/user/dashboard/${nextSectionSlug}`);
      setCurrentStep(nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      resetFormData();
      setIsEditingSection(false);
      handleGlobalClick();
      dispatch(setHasUnsavedChanges(false));
      dispatch(setSectionId(formPages[currentStep - 1].section_id));
      const prevStep = currentStep - 1;
      // Use section name slug in the URL
      const prevSectionSlug = getSectionSlug(formPages[prevStep].section_name);
      navigate(`/user/dashboard/${prevSectionSlug}`);
      setCurrentStep(prevStep);
    }
  };

  // Payload for Saving Progress in perticular section
  const fieldsPayload: any[] = [];

  // Process main fields (non-conditional)
  mainFields.forEach((field: any) => {
    if (
      field.field_id &&
      field?.field_type !== 'condition' &&
      field?.field_type !== 'condition_with_multiple_fields' &&
      field?.field_type !== 'condition_with_table' &&
      field?.field_type !== 'file'
    ) {
      // Ensure field.id exists

      fieldsPayload.push({
        field_id: field.field_id,
        value: formValues[field.field_id] !== undefined ? String(formValues[field.field_id]) : '',
      });
    }
  });

  // Process conditional fields with the new format
  Object.entries(conditionalValues).forEach(([fieldId, value]) => {
    // Skip entries with empty fieldId

    if (!fieldId) {
      console.warn('Skipping conditional value with empty field_id', value);
      return;
    }

    // Check if value is an object with nested_field structure
    if (value && typeof value === 'object' && 'value' in value && value.type !== 'file') {
      const fieldPayload: any = {
        field_id: fieldId,
        value: String(value.value),
      };

      // Payload for nested Tables in conditional fields
      if (value.nested_table && value.nested_table.table_id) {
        fieldPayload.nested_table = {
          table_id: value.nested_table.table_id,
          values: value.nested_table.values,
        };
      }

      // Add nested multiple files upload
      if (value?.nested_field) {
        if (value.nested_field.files?.length !== 0) {
          fieldPayload.nested_field = {
            field_id: value.nested_field_field_id,
            value: String(value?.nested_field?.value || ''),
            files: value.nested_field.files,
          };
        } else {
          fieldPayload.nested_field = {
            field_id: value.nested_field_field_id,
            value: String(value?.nested_field?.value || ''),
            files: [],
          };
        }
      }
      fieldsPayload.push(fieldPayload);
    } else if (value.type === 'file') {
      fieldsPayload.push({
        field_id: fieldId,
        files: value?.files ?? [],
      });
    } else if (value !== undefined && value?.fields[0]?.type !== 'condition_with_multiple_fields') {
      // Handle legacy format or simple value
      fieldsPayload.push({
        field_id: fieldId,
        value: String(value || ''),
      });
    } else if (value?.fields[0]?.type === 'condition_with_multiple_fields' && value !== undefined) {
      fieldsPayload.push({
        field_id: value?.fields[0]?.field_id,
        nested_fields: value?.fields[0]?.nested_fields,
        value: value?.fields[0]?.value,
      });
    }
  });

  // Table Payload
  const tablesPayload = Object?.entries(tablesData)?.map(([tableId, values]) => ({
    table_id: tableId,
    values: values?.map((item: any) => ({
      ...item,
    })),
    nestedFieldValue: fieldValue ? Object.entries(fieldValue)[0]?.[1] : undefined,
    nestedFieldValue46: fieldValue ? Object.entries(fieldValue)[1]?.[1] : undefined,
  }));
  // Tabs Payload
  const tabPayload = transformDataFormat(
    Object.entries(tabsData).map(([tab_field_id, tab_responses]) => ({
      tab_field_id,
      tab_responses,
    }))
  );

  const payload = {
    fields: fieldsPayload,
    tables: tablesPayload,
    tabFields: tabPayload?.tabFields,
  };

  const handleSaveProgress = async () => {
    try {
      await updateSection({
        districtId: districtId,
        sectionId: sectionId || '',
        payload,
      })
        .then((res) => {
          if (res.data.statusCode == 200) {
            if (isUnsavedProgressPopupNextVisible) {
              handleNext();
              dispatch(setHasUnsavedChanges(false));
            }
            if (isUnsavedProgressPopupPreviousVisible) {
              handlePrevious();
              dispatch(setHasUnsavedChanges(false));
            }
            dispatch(setHasUnsavedChanges(false));
            if (dataTableUpdated) {
              addToast('Table updated successfully', 'Table data updated successfully.', 'success');
              dispatch(setDataTableUpdated(false));
            } else {
              addToast(
                'Progress saved successfully',
                'Progress is saved successfully. You can continue editing or can resume later to complete the form.',
                'success'
              );
            }
            // PostHog event tracking for form progress saved
            trackEvent(PostHogEvents.FORM_PROGRESS_SAVED, {
              form_id: sectionId,
              user_id: email,
              timestamp: new Date().toISOString(),
            });
          }
        })
        .catch((error) => {
          trackEvent(PostHogEvents.FORM_PROGRESS_SAVED_FAILED, {
            form_id: sectionId,
            user_id: email,
            timestamp: new Date().toISOString(),
          });
          console.error('Error saving progress:', error);
          addToast(
            'Failed to save progress',
            'Failed to save progress. Please try again or check your connection.',
            'error'
          );
        });
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  // Helper function to check if field type should be rendered in grid
  const shouldBeInGrid = (fieldType: string) => {
    return ['text_input', 'dropdown', 'date'].includes(fieldType);
  };
  const handleCloseUnsavedProgressPopupNext = () => {
    setUnsavedProgressPopupNextVisible(false);
  };
  const handleCloseUnsavedProgressPreviousPopup = () => {
    setUnsavedProgressPopupPreviousVisible(false);
  };

  const excludedSections = ['Instructions', 'Case Information', 'Supplemental Case Information'];
  const resubmitStatuses = [
    'user_review_requested',
    'user_edits_requested',
    'review_and_edits_requested',
    'in_progress',
  ];

  const shouldShowButton =
    fieldData?.data?.name &&
    !excludedSections.includes(fieldData.data.name) &&
    resubmitStatuses.includes(factSheetStatus ?? '');

  if (isError) {
    return <ErrorPage />;
  }
  // Helper function to check if field or its nested fields should be highlighted
  // In your UserDynamicForm component, add this helper function with logging:
  const shouldHighlightOption = (option: any) => {
    const statusRequiresHighlight =
      factSheetStatus &&
      ['user_review_requested', 'review_and_edits_requested', 'user_edits_requested'].includes(
        factSheetStatus
      );

    if (!statusRequiresHighlight) return false;

    // Check if option itself is marked for review
    if (option.is_admin_review === true) return true;

    // Check the fields array within the option (this is the key part for your data)
    if (option.fields && Array.isArray(option.fields)) {
      return option.fields.some((field: any) => field.is_admin_review === true);
    }

    return false;
  };

  // New function to check if nested fields should be highlighted
  const shouldHighlightNestedField = (nestedField: any) => {
    const statusRequiresHighlight =
      factSheetStatus &&
      ['user_review_requested', 'review_and_edits_requested', 'user_edits_requested'].includes(
        factSheetStatus
      );

    if (!statusRequiresHighlight) return false;

    return nestedField.is_admin_review === true;
  };

  const shouldHighlightFieldInOption = (field: any) => {
    const statusRequiresHighlight =
      factSheetStatus &&
      ['user_review_requested', 'review_and_edits_requested', 'user_edits_requested'].includes(
        factSheetStatus
      );

    if (!statusRequiresHighlight) return false;

    return field.is_admin_review === true;
  };

  const shouldHighlightComplexField = (field: any) => {
    const statusRequiresHighlight =
      factSheetStatus &&
      ['user_review_requested', 'review_and_edits_requested', 'user_edits_requested'].includes(
        factSheetStatus
      );

    if (!statusRequiresHighlight) return false;

    // Check if the main field is marked for review
    if (field.is_admin_review === true) {
      return true;
    }

    // For condition_with_multiple_fields, check the exact structure from your JSON
    if (field.field_type === 'condition_with_multiple_fields' && field.options) {
      return field.options.some((option: any) => {
        // Check if option itself is marked for review
        if (option.is_admin_review === true) return true;

        // Check the fields array within each option (this matches your JSON structure)
        if (option.fields && Array.isArray(option.fields)) {
          return option.fields.some((nestedField: any) => nestedField.is_admin_review === true);
        }

        return false;
      });
    }

    return false;
  };
  return (
    <>
      <CollaborationPopup
        isVisible={showCollaborationPopup}
        onClose={() => setShowCollaborationPopup(false)}
      />
      {isFieldDataLoading || isApiFetching ? (
        <div className="flex justify-center items-center h-full w-full">
          <Loader color="primary" size="xl" />
        </div>
      ) : (
        <div
          className="flex flex-col h-full"
          ref={editContainerRef}
          onClick={(e) => {
            // Prevent event from bubbling up if needed
            e.stopPropagation();
            handleGlobalClick();
          }}
        >
          {/* Show Data based on locked user  */}
          <div
            className={`relative overflow-y-auto h-[80%] ${lockedOwner != '' && !isLockedByMe ? 'cursor-not-allowed' : ''}  m-6 py-2`}
          >
            {action == 'acquire' && lockedOwner != '' && !isReviewForm && (
              <div className="translate-y-2 flex items-center mb-4">
                <div className="absolute -top-3 left-4 z-10">
                  <span className="inline-flex items-center justify-center border border-blue-100 rounded-full bg-ds-mc-blue-100 p-2 text-sm text-blue-100 font-medium">
                    {lockedOwner
                      ?.split(' ')
                      .map((word) => word[0])
                      .join('')
                      .toUpperCase()}
                  </span>
                </div>
                <div className="absolute -top-3 left-16 z-10">
                  <span className="inline-block border bg-ds-mc-blue-100 border-blue-100 rounded-md px-3 py-2 text-sm text-blue-100 font-medium whitespace-nowrap">
                    {isLockedByMe ? 'You are editing' : `${lockedOwner} is editing`}
                  </span>
                </div>
              </div>
            )}

            <div
              className={` my-2 py-6 ${action == 'acquire' && lockedOwner != '' && !isReviewForm ? 'border border-blue-100' : ''}  rounded-lg`}
            >
              <div
                className="h-full w-full px-8  "
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    fieldData?.data?.name !== 'Instructions' &&
                    fieldData?.data.name !== 'Case Information' &&
                    fieldData?.data.name !== 'Supplemental Case Information' &&
                    !isReviewForm
                  ) {
                    handleEdit(sectionId);
                  }
                }}
              >
                <div className="font-semibold flex flex-col gap-1 text-ds-gray-800 border-b py-2 mb-2 w-full border-blue-2100">
                  <h1>{fieldData?.data?.name ?? ''}</h1>
                  <h2 className="text-ds-gray-400 text-xs font-normal">
                    {fieldData?.data?.description}
                  </h2>
                </div>
                {/* Making Hard coded Instructions and Submission section */}
                {fieldData?.data?.name === 'Submission' ? (
                  <Certifications
                    ref={certificationsRef}
                    setIsFormSubmitted={setIsFormSubmitted}
                    factSheetStatus={factSheetStatus}
                  />
                ) : (
                  <div className="space-y-6 gap-2">
                    {/* Rendering logic for components with proper type detection */}
                    {(() => {
                      const elements = [];
                      let gridFieldsGroup = [];

                      for (const component of allComponents) {
                        if (shouldBeInGrid(component.field_type)) {
                          // Add field to the current group
                          gridFieldsGroup.push(component);

                          // When we have 2 grid fields, render them in a row
                          if (gridFieldsGroup.length === 2) {
                            elements.push(
                              <div
                                key={`group-${gridFieldsGroup[0].field_id}`}
                                className="grid grid-cols-2 xl:grid-cols-2 md:grid-cols-1 gap-10"
                              >
                                {gridFieldsGroup.map((input) => (
                                  <div key={input.field_id}>
                                    {renderField(component, shouldHighlightComplexField(component))}
                                  </div>
                                ))}
                              </div>
                            );
                            gridFieldsGroup = [];
                          }
                        } else {
                          // If we have pending grid fields (1 remaining), render them first
                          if (gridFieldsGroup.length > 0) {
                            elements.push(
                              <div
                                key={`group-${gridFieldsGroup[0].field_id}`}
                                className="grid grid-cols-2 xl:grid-cols-2 lg:grid-cols-1 gap-4 mr-10"
                              >
                                {gridFieldsGroup.map((input) => (
                                  <div key={input.field_id}>
                                    {renderField(input, shouldHighlightComplexField(input))}{' '}
                                  </div>
                                ))}
                              </div>
                            );
                            gridFieldsGroup = [];
                          }

                          // Render non-grid components based on their type
                          if (component.field_type) {
                            // This is a regular field
                            elements.push(
                              <div key={component.field_id} className="col-span-3">
                                {renderField(
                                  component,
                                  (factSheetStatus === 'user_review_requested' ||
                                    factSheetStatus === 'review_and_edits_requested' ||
                                    factSheetStatus === 'user_edits_requested') &&
                                    component.is_admin_review
                                )}
                              </div>
                            );
                          } else if (component?.table_id) {
                            // This is a data table
                            elements.push(
                              <div
                                key={component.field_id || `table-${component.id}`}
                                className={`w-full mt-6`}
                              >
                                <CustomDataTable
                                  tableData={component}
                                  onTableDataChange={handleTableDataChange}
                                  handleSaveProgress={handleSaveProgress}
                                  isLockedByMe={isLockedByMe}
                                  lockedOwner={lockedOwner}
                                  isReviewForm={isReviewForm}
                                  collaborationPopup={setShowCollaborationPopup}
                                />
                              </div>
                            );
                          } else if (component?.tab_field_id) {
                            // This is a tabs table
                            const shouldHighlight = shouldHighlightTabField(component);
                            elements.push(
                              <div
                                key={component.id || `tabs-${elements.length}`}
                                className={`w-full mt-6`}
                              >
                                <CustomTabsWithTable
                                  formData={component}
                                  onDataChange={handleTabsDataChange}
                                  isLockedByMe={isLockedByMe}
                                  lockedOwner={lockedOwner}
                                  isReviewForm={isReviewForm}
                                  collaborationPopup={setShowCollaborationPopup}
                                  // Pass highlighting functions to the component
                                  shouldHighlightTab={shouldHighlightTab}
                                  shouldHighlightTable={shouldHighlightTable}
                                  shouldHighlight={shouldHighlight}
                                />
                              </div>
                            );
                          }
                        }
                      }

                      // Render any remaining grid fields
                      if (gridFieldsGroup.length > 0) {
                        elements.push(
                          <div
                            key={`group-${gridFieldsGroup[0].field_id}`}
                            className="grid grid-cols-1 md:grid-cols-2 gap-4"
                          >
                            {gridFieldsGroup.map((input) => (
                              <div key={input.field_id}>
                                {renderField(
                                  input,
                                  (factSheetStatus === 'user_review_requested' ||
                                    factSheetStatus === 'review_and_edits_requested') &&
                                    input.is_admin_review
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      }

                      return elements;
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Unsaved Progress Popup */}
          <UnSavedProgressPopupNext
            handleNext={handleNext}
            handleSaveProgress={handleSaveProgress}
            onClose={handleCloseUnsavedProgressPopupNext}
            isVisible={isUnsavedProgressPopupNextVisible}
            loading={isUpdateSectionLoading}
          />

          <UnSavedProgressPopupPrevious
            handlePrevious={handlePrevious}
            handleSaveProgress={handleSaveProgress}
            onClose={handleCloseUnsavedProgressPreviousPopup}
            isVisible={isUnsavedProgressPopupPreviousVisible}
            loading={isUpdateSectionLoading}
          />
          {/* Footer buttons */}
          <div className="flex justify-end px-4  border-t h-16 my-auto">
            <div className="flex gap-2 items-center my-auto">
              {fieldData?.data?.name !== 'Submission' &&
                fieldData?.data?.name !== 'Instructions' &&
                !isReviewForm &&
                fieldData?.data.name !== 'Case Information' &&
                fieldData?.data?.name !== 'Supplemental Case Information' && (
                  <Button
                    loading={dataTableUpdated ? false : isUpdateSectionLoading}
                    onClick={handleSaveProgress}
                    color="success"
                    size="lg"
                  >
                    Save Progress
                  </Button>
                )}

              {/* Logic for Perticular Section */}
              {(fieldData?.data.name == 'Case Information' ||
                fieldData?.data.name == 'Supplemental Case Information') && (
                <Button color="outlined" size="sm" onClick={handlePrevious}>
                  Back
                </Button>
              )}

              {(fieldData?.data?.name == 'Instructions' ||
                fieldData?.data.name == 'Case Information' ||
                fieldData?.data.name == 'Supplemental Case Information') && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleNext}
                  disabled={
                    fieldData?.data.name == 'Supplemental Questions' ? certificationDisabled : false
                  }
                >
                  Next
                </Button>
              )}

              {/* For Dynamic Sections  */}

              {Number(currentStep) > 0 &&
                fieldData?.data?.name !== 'Instructions' &&
                fieldData?.data.name !== 'Case Information' &&
                fieldData?.data.name !== 'Supplemental Case Information' && (
                  <Button
                    size="sm"
                    onClick={() => {
                      if (isReviewForm || !hasUnsavedChanges) {
                        handlePrevious();
                      } else {
                        setUnsavedProgressPopupPreviousVisible(true);
                      }
                    }}
                    color="outlined"
                  >
                    Back
                  </Button>
                )}

              {Number(currentStep) < (formPages?.length || 0) - 1 &&
              fieldData?.data?.name !== 'Instructions' &&
              fieldData?.data.name !== 'Case Information' &&
              fieldData?.data.name !== 'Supplemental Case Information' ? (
                <Button
                  onClick={() => {
                    if (isReviewForm || !hasUnsavedChanges) {
                      handleNext();
                    } else {
                      setUnsavedProgressPopupNextVisible(true);
                    }
                  }}
                  size="sm"
                  color="primary"
                  disabled={
                    fieldData?.data.name == 'Interference with Property'
                      ? certificationDisabled
                      : false
                  }
                >
                  Next
                </Button>
              ) : shouldShowButton ? (
                <Button onClick={handleClick} size="md" loading={isFormSubmitted} color="primary">
                  Submit
                </Button>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserDynamicForm;
